import { MoneySchema } from "@redotech/money";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { trackerStatusSchema } from "./order-tracking-tracker-status-definition";
import { trackingLocationSchema } from "./order-tracking-tracking-location-definition";
import {
  OrderTrackingOrderFulfillmentStatus,
  orderTrackingOrderSources,
  OrderTrackingShipmentStatus,
} from "./order-tracking-types";

const trackingPageEventSchema_ = z.object({
  timestamp: z.coerce.date(),
  status: trackerStatusSchema,
  message: z.string(),
  location: trackingLocationSchema.nullish(),
});
export const trackingPageEventSchema: z.ZodType<TrackingPageEvent> =
  trackingPageEventSchema_;
export interface TrackingPageEvent
  extends z.infer<typeof trackingPageEventSchema_> {}

const trackingPageLineItemSchema_ = z.object({
  id: z.string(),
  name: z.string().nullish(),
  variantTitle: z.string().nullish(),
  quantity: z.number(),
  /** Unit price before discounts. */
  undiscountedPrice: MoneySchema.nullish(),
  /** Unit price after line-item discounts. Null when no discount or data unavailable. */
  discountedPrice: MoneySchema.nullish(),
  imageUrl: z.string().nullish(),
  productId: z.string().nullish(),
  variantId: z.string().nullish(),
});
export const trackingPageLineItemSchema: z.ZodType<TrackingPageLineItem> =
  trackingPageLineItemSchema_;
export interface TrackingPageLineItem
  extends z.infer<typeof trackingPageLineItemSchema_> {}

const trackingPageShipmentSchema_ = z.object({
  id: z.string(),
  status: createEnum(OrderTrackingShipmentStatus),
  trackingCode: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  carrier: z.string().nullish(),
  estimatedDeliveryAt: z.coerce.date().nullish(),
  deliveredAt: z.coerce.date().nullish(),
  updatedAt: z.coerce.date(),
  trackingEvents: z.array(trackingPageEventSchema),
  lineItems: z.array(trackingPageLineItemSchema),
});
export const trackingPageShipmentSchema: z.ZodType<TrackingPageShipment> =
  trackingPageShipmentSchema_;
export interface TrackingPageShipment
  extends z.infer<typeof trackingPageShipmentSchema_> {}

const trackingPageCustomerSchema_ = z.object({
  email: z.string().nullish(),
  name: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  phone: z.string().nullish(),
});
export const trackingPageCustomerSchema: z.ZodType<TrackingPageCustomer> =
  trackingPageCustomerSchema_;
export interface TrackingPageCustomer
  extends z.infer<typeof trackingPageCustomerSchema_> {}

const trackingPageShippingAddressSchema_ = z.object({
  streetAddress: z.string().nullish(),
  city: z.string().nullish(),
  state: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
  postalCode: z.string().nullish(),
});
export const trackingPageShippingAddressSchema: z.ZodType<TrackingPageShippingAddress> =
  trackingPageShippingAddressSchema_;
export interface TrackingPageShippingAddress
  extends z.infer<typeof trackingPageShippingAddressSchema_> {}

const trackingPageOrderSchema_ = z.object({
  externalOrderId: z.string(),
  internalMongoId: z.string(),
  teamId: z.string(),
  orderNumber: z.string().nullish(),
  customer: trackingPageCustomerSchema,
  /** Shopify's `source_name` — the sales channel that created the order (e.g. "web", "pos", "shopify_draft_order"). */
  sourceName: z.string().nullish(),
  cancelReason: z.string().nullish(),
  cancelledAt: z.coerce.date().nullish(),
  fulfillmentStatus: createEnum(OrderTrackingOrderFulfillmentStatus),
  currency: z.string(),
  /** The e-commerce provider/platform (e.g. "shopify", "commentsold", "bigcommerce"). */
  source: createEnum(orderTrackingOrderSources),
  shippingAddress: trackingPageShippingAddressSchema.nullish(),
  shippingMethod: z.string().nullish(),
  shipments: z.array(trackingPageShipmentSchema),
  lineItems: z.array(trackingPageLineItemSchema),
  returnDeeplink: z.string().nullish(),
  claimDeeplink: z.string().nullish(),
});
export const trackingPageOrderSchema: z.ZodType<TrackingPageOrder> =
  trackingPageOrderSchema_;
export interface TrackingPageOrder
  extends z.infer<typeof trackingPageOrderSchema_> {}
